import { Button, makeStyles, mergeClasses, Select, Text, tokens } from '@fluentui/react-components';
import { ChevronLeftRegular, ChevronRightRegular } from '@fluentui/react-icons';
import * as React from 'react';

const useStyles = makeStyles({
    pager: {
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '12px 16px',
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground2,
        flexWrap: 'wrap',
    },
    range: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2,
    },
    rangeStrong: {
        color: tokens.colorNeutralForeground1,
        fontWeight: tokens.fontWeightBold,
    },
    spacer: {
        flex: 1,
    },
    pages: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    pageBtn: {
        minWidth: '32px',
        height: '32px',
        padding: '0 6px',
    },
    pageBtnActive: {
        backgroundColor: tokens.colorBrandBackground,
        color: tokens.colorNeutralForegroundOnBrand,
        ':hover': {
            backgroundColor: tokens.colorBrandBackgroundHover,
            color: tokens.colorNeutralForegroundOnBrand,
        },
    },
    ellipsis: {
        color: tokens.colorNeutralForeground3,
        padding: '0 2px',
    },
    pageSize: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2,
    },
    select: {
        minWidth: '72px',
    },
});

type PageToken = number | 'ellipsis';

/** Numbered page list with `…` truncation (1-based), matching the prototype. */
const buildPageTokens = (current: number, pages: number): PageToken[] => {
    if (pages <= 7) {
        return Array.from({ length: pages }, (_, i) => i + 1);
    }
    const tokensList: PageToken[] = [1];
    if (current > 3) tokensList.push('ellipsis');
    const lo = Math.max(2, current - 1);
    const hi = Math.min(pages - 1, current + 1);
    for (let p = lo; p <= hi; p++) tokensList.push(p);
    if (current < pages - 2) tokensList.push('ellipsis');
    tokensList.push(pages);
    return tokensList;
};

interface PaginationToolbarProps {
    /** Zero-based current page index. */
    page: number;
    pageSize: number;
    /** Total rows across all pages; when omitted, falls back to prev/next only. */
    total?: number;
    /** Number of items on the current page (drives Next in fallback mode). */
    itemCount: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
    pageSizeOptions?: number[];
}

const PaginationToolbar = ({
    page,
    pageSize,
    total,
    itemCount,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = [10, 20, 50],
}: PaginationToolbarProps) => {
    const styles = useStyles();
    const hasTotal = total !== undefined;

    const current1 = page + 1;
    const start = itemCount === 0 ? 0 : page * pageSize + 1;
    const end = hasTotal ? Math.min(total, page * pageSize + itemCount) : page * pageSize + itemCount;
    const pages = hasTotal ? Math.max(1, Math.ceil(total / pageSize)) : undefined;

    const canPrev = page > 0;
    const canNext = hasTotal ? current1 < (pages as number) : itemCount === pageSize;

    return (
        <div className={styles.pager}>
            <Text className={styles.range}>
                Showing <span className={styles.rangeStrong}>{start}–{end}</span>
                {hasTotal && (
                    <>
                        {' '}of <span className={styles.rangeStrong}>{total}</span>
                    </>
                )}{' '}
                clients
            </Text>
            <span className={styles.spacer} />

            <div className={styles.pages}>
                <Button
                    appearance="subtle"
                    size="small"
                    className={styles.pageBtn}
                    icon={<ChevronLeftRegular />}
                    aria-label="Previous page"
                    disabled={!canPrev}
                    onClick={() => canPrev && onPageChange(page - 1)}
                />
                {hasTotal &&
                    buildPageTokens(current1, pages as number).map((tok, i) =>
                        tok === 'ellipsis' ? (
                            <span key={`e${i}`} className={styles.ellipsis}>
                                …
                            </span>
                        ) : (
                            <Button
                                key={tok}
                                appearance="subtle"
                                size="small"
                                className={mergeClasses(styles.pageBtn, tok === current1 && styles.pageBtnActive)}
                                onClick={() => onPageChange(tok - 1)}
                                aria-current={tok === current1 ? 'page' : undefined}
                            >
                                {tok}
                            </Button>
                        ),
                    )}
                {!hasTotal && (
                    <Button appearance="subtle" size="small" className={mergeClasses(styles.pageBtn, styles.pageBtnActive)} disabled>
                        {current1}
                    </Button>
                )}
                <Button
                    appearance="subtle"
                    size="small"
                    className={styles.pageBtn}
                    icon={<ChevronRightRegular />}
                    aria-label="Next page"
                    disabled={!canNext}
                    onClick={() => canNext && onPageChange(page + 1)}
                />
            </div>

            {onPageSizeChange && (
                <span className={styles.pageSize}>
                    Rows
                    <Select
                        className={styles.select}
                        size="small"
                        value={String(pageSize)}
                        onChange={(_e: React.ChangeEvent<HTMLSelectElement>, data) =>
                            onPageSizeChange(Number(data.value))
                        }
                    >
                        {pageSizeOptions.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </Select>
                </span>
            )}
        </div>
    );
};

export default PaginationToolbar;
